import type { CreateReleaseDto, ReleaseDto, UpdateReleaseDto } from '@/castapi';
import { AppLogger } from '@/logger';
import { getErrorMessage, getReleasesApi } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';

const logger = new AppLogger('adminReleases state');

interface IAdminReleasesState {
  releases: ReleaseDto[];
  releasesLoading: boolean;
  releasesLoadError?: string;
  releaseSaving: boolean;
  releaseSaveError?: string;
}

const initialState = (): IAdminReleasesState => ({
  releases: [],
  releasesLoading: false,
  releasesLoadError: undefined,
  releaseSaving: false,
  releaseSaveError: undefined,
});

type ActionParams = IActionParams<IAdminReleasesState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    RELEASES_LOADING(state: IAdminReleasesState): void {
      state.releasesLoadError = undefined;
      state.releasesLoading = true;
    },
    RELEASES_LOADED(state: IAdminReleasesState, payload: ReleaseDto[]): void {
      state.releases = payload;
      state.releasesLoading = false;
    },
    RELEASES_LOAD_ERROR(state: IAdminReleasesState, error: Error): void {
      state.releasesLoadError = getErrorMessage(error);
      state.releasesLoading = false;
    },
    RELEASE_ADD_IN_PROGRESS(state: IAdminReleasesState): void {
      state.releaseSaving = true;
      state.releaseSaveError = undefined;
    },
    RELEASE_ADD_SUCCESS(state: IAdminReleasesState, payload: ReleaseDto): void {
      state.releases.push(payload);
      state.releaseSaving = false;
    },
    RELEASE_ADD_ERROR(state: IAdminReleasesState, error: Error): void {
      state.releaseSaving = false;
      state.releaseSaveError = getErrorMessage(error);
    },
    RELEASE_UPDATE_IN_PROGRESS(state: IAdminReleasesState): void {
      state.releaseSaving = true;
      state.releaseSaveError = undefined;
    },
    RELEASE_UPDATE_SUCCESS(state: IAdminReleasesState, payload: ReleaseDto): void {
      const ind = state.releases.findIndex(item => item.id === payload.id);
      Object.assign(state.releases[ind], payload);
      state.releaseSaving = false;
    },
    RELEASE_UPDATE_ERROR(state: IAdminReleasesState, error: Error): void {
      state.releaseSaving = false;
      state.releaseSaveError = getErrorMessage(error);
    },
    RELEASE_DELETE_IN_PROGRESS(state: IAdminReleasesState): void {
      state.releaseSaving = true;
      state.releaseSaveError = undefined;
    },
    RELEASE_DELETE_SUCCESS(state: IAdminReleasesState, id: number): void {
      state.releases = state.releases.filter(item => item.id !== id);
      state.releaseSaving = false;
    },
    RELEASE_DELETE_ERROR(state: IAdminReleasesState, error: Error): void {
      state.releaseSaving = false;
      state.releaseSaveError = getErrorMessage(error);
    },
    RESET_SAVING_STATUS(state: IAdminReleasesState): void {
      state.releaseSaving = false;
      state.releaseSaveError = undefined;
    },
    RESET_STATE(state: IAdminReleasesState): void {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async loadReleases({ commit, rootGetters }: ActionParams): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      try {
        commit('RELEASES_LOADING');
        const response = await getReleasesApi(accessToken).releaseControllerFindAll();
        commit('RELEASES_LOADED', response.data);
      } catch (error) {
        commit('RELEASES_LOAD_ERROR', error);
        logger.captureStoreError('loadReleases', error);
      }
    },
    async addRelease({ commit, dispatch, rootGetters }: ActionParams, data: CreateReleaseDto): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      commit('RELEASE_ADD_IN_PROGRESS');
      try {
        const response = await getReleasesApi(accessToken).releaseControllerCreate(data);
        commit('RELEASE_ADD_SUCCESS', response.data);
        await dispatch('loadReleases');
      } catch (error) {
        commit('RELEASE_ADD_ERROR', error);
        logger.captureStoreError('addRelease', error, { data });
      }
    },
    async updateRelease({ commit, dispatch, rootGetters }: ActionParams, data: UpdateReleaseDto): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      commit('RELEASE_UPDATE_IN_PROGRESS');
      try {
        await getReleasesApi(accessToken).releaseControllerUpdate(data.id, data);
        commit('RELEASE_UPDATE_SUCCESS', data);
        await dispatch('loadReleases');
      } catch (error) {
        commit('RELEASE_UPDATE_ERROR', error);
        logger.captureStoreError('updateRelease', error, { data });
      }
    },
    async removeRelease({ commit, dispatch, rootGetters }: ActionParams, id: number): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      commit('RELEASE_DELETE_IN_PROGRESS');
      try {
        await getReleasesApi(accessToken).releaseControllerRemove(id);
        commit('RELEASE_DELETE_SUCCESS', id);
        await dispatch('loadReleases');
      } catch (error) {
        commit('RELEASE_DELETE_ERROR', error);
        logger.captureStoreError('removeRelease', error, { id });
      }
    },
    resetSavingStatus({ commit }: ActionParams): void {
      commit('RESET_SAVING_STATUS');
    },
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
  },
  getters: {
    releases: (state: IAdminReleasesState): ReleaseDto[] => state.releases,
    releasesLoading: (state: IAdminReleasesState): boolean => state.releasesLoading,
    releasesLoadError: (state: IAdminReleasesState): undefined | string => state.releasesLoadError,
    releaseSaving: (state: IAdminReleasesState): boolean => state.releaseSaving,
    releaseSaveError: (state: IAdminReleasesState): undefined | string => state.releaseSaveError,
  },
};
